/**
 * Page component is a React component that handles the rendering of meta tags
 * and structured data for SEO purposes. It uses the `react-helmet` library to
 * dynamically update the document head with meta information.
 * 
 * @class
 * @extends {React.Component}
 * 
 * @property {Object} props - The props passed to the component.
 * @property {React.ReactNode} props.children - The child components to be rendered inside the Page component.
 * @property {string} [props.id] - The id attribute for the root div of the component.
 * @property {string} [props.className] - The className attribute for the root div of the component.
 * @property {Object} [props.style] - The style object for the root div of the component.
 * @property {Object} [props.product] - The product object containing details about the product.
 * @property {string} [props.product.seo_desc] - The SEO description of the product.
 * @property {string} [props.product.mat_desc] - The material description of the product.
 * @property {string} [props.product.brand_en] - The brand name of the product in English.
 * @property {string} [props.product.mat_name] - The material name of the product.
 * @property {string} [props.product.mat_name_th] - The material name of the product in Thai.
 * @property {Object} [props.product.comments] - The comments related to the product.
 * @property {Array} [props.product.comments.data] - The array of comment objects.
 * @property {number} [props.product.rating] - The rating of the product.
 * @property {number} [props.product.rating_count] - The number of ratings for the product.
 * @property {boolean} [props.product.sold_out] - Indicates if the product is sold out.
 * @property {string} [props.product.mat_id] - The material ID of the product.
 * @property {string} [props.product.item_id] - The item ID of the product.
 * @property {string} [props.product.image_1] - The first image URL of the product.
 * @property {string} [props.product.image_2] - The second image URL of the product.
 * @property {string} [props.product.image_3] - The third image URL of the product.
 * @property {string} [props.product.image_4] - The fourth image URL of the product.
 * @property {string} [props.product.image_5] - The fifth image URL of the product.
 * @property {Object} [props.location] - The location object provided by `react-router`.
 * @property {string} [props.location.pathname] - The current pathname of the route.
 * 
 * @method getImageType
 * @description Determines the MIME type of an image based on its file extension.
 * @param {string} type - The file extension of the image (e.g., "png", "jpg").
 * @returns {string} The MIME type of the image.
 * 
 * @method extractType
 * @description Extracts the file extension from a given filename or URL.
 * @param {string} text - The filename or URL to extract the extension from.
 * @returns {Array|null} The result of the regular expression match.
 * 
 * @method getMetaTags
 * @description Generates an array of meta tag objects for SEO purposes.
 * @param {Object} metaProps - The properties used to generate meta tags.
 * @param {string} [metaProps.title] - The title of the page.
 * @param {boolean} [metaProps.noTailTitle] - Whether to exclude the default title suffix.
 * @param {string} [metaProps.description] - The description of the page.
 * @param {Object|string} [metaProps.image] - The image URL or object for the page.
 * @param {number} [metaProps.imageWidth] - The width of the image.
 * @param {number} [metaProps.imageHeight] - The height of the image.
 * @param {string} [metaProps.imageType] - The MIME type of the image.
 * @param {string} [metaProps.twitter] - The Twitter handle for the page.
 * @param {boolean} [metaProps.noCrawl] - Whether to prevent search engines from indexing the page.
 * @param {string} [metaProps.published] - The published date of the content.
 * @param {string} [metaProps.updated] - The updated date of the content.
 * @param {string} [metaProps.category] - The category of the content.
 * @param {string} [metaProps.tags] - The tags associated with the content.
 * @param {string} [metaProps.keyword] - The keywords for the page.
 * @param {string} [metaProps.keywords] - Additional keywords for the page.
 * @param {boolean} [metaProps.noDefaultKeywords] - Whether to exclude default keywords.
 * @param {string} pathname - The current pathname of the route.
 * @returns {Array<Object>} An array of meta tag objects.
 * 
 * @method replaceAll
 * @description Replaces all occurrences of a substring with a replacement string.
 * @param {string} original - The original string.
 * @param {string} search - The substring to search for.
 * @param {string} replacement - The string to replace the substring with.
 * @returns {string} The modified string with replacements.
 * 
 * @method render
 * @description Renders the Page component, including meta tags and structured data.
 * @returns {React.ReactNode} The rendered component.
 */
import React, { Component } from 'react';
import { withRouter } from 'react-router';
import Helmet from 'react-helmet';
import _ from 'lodash';
import moment from 'moment';
// import {BRANCHES} from '../assets/constant'
import { isArray } from 'util';
import { convertImageToWebP } from './common/webpUtils';

const MAX_PRO = "70%"

// Safe env access to avoid ReferenceError when bundler doesn't shim process
const getEnvVar = (key) => (typeof process !== 'undefined' && process.env && process.env[key]) || undefined;
const nodeEnv = (getEnvVar('NODE_ENV') || 'production').toLowerCase();
const isDev = nodeEnv === 'development';

const SITE_URL = isDev ? 'http://localhost:3000' : 'https://thebeautrium.com';

const CDN_URL = isDev ? 'http://localhost:3000' : 'https://cdn.thebeautrium.com';

const FACEBOOK_APP_ID = isDev ? '202800687523359' : '157875898136228';
const FACEBOOK_PAGE_ID = '157252584407116';

const defaultTitle = `Beautrium บิวเทรี่ยม | น้ำหอม เครื่องสำอาง สกินแคร์ ของแท้ ส่งเร็ว`;
const defaultDescription = `บิวเทรี่ยมร้านขายเครื่องสำอาง สกินแคร์ น้ำหอม ของแท้มาแรงอันดับ 1 ของประเทศไทย ส่งเร็ว ส่งดี ส่งไว พร้อมกับโปรโมชั่นลดสูงสุด ${MAX_PRO} แถมคูปองมากมาย และมีหน้าร้านมากกว่า 13 สาขาทั่วกรุงเทพและต่างจังหวัด`;
const defaultImage = `https://cdn.thebeautrium.com/web-image/og/social_share-202106.jpg`;
const defaultTwitter = '@thebeautrium';
const defaultSep = ' | ';
const defaultKeyword = 'Beautrium, บิวเทรี่ยม, beauty, บิวตี้, cosmetics, skincare, เครื่องสำอาง, เครื่องสำอางค์, สกินแคร์, โปรโมชั่น, Promotion, สวย, ดูแลเส้นผม, อุปกรณ์เสริม, ดูแลผิวกาย, น้ำหอม, Perfume, อาหารเสริม, เครื่องสำอางสำหรับผู้ชาย, ดูแลดวงตา, ของใช้ส่วนตัว, เวชสำอางค์'

class Page extends Component {

    getImageType(type) {
        if (type === "png") {
            return 'image/png';
        } else if (type === "jpg" || type === "jpeg") {
            return 'image/jpeg';
        }
    }

    extractType(text) {
        var re = /(?:\.([^.]+))?$/;
        return re.exec(text)
    }

    getMetaTags({
        title,
        noTailTitle,
        description,
        image,
        imageWidth,
        imageHeight,
        imageType,
        // contentType,
        twitter,
        noCrawl,
        published,
        updated,
        category,
        tags,
        keyword,
        keywords,
        noDefaultKeywords
    }, pathname) {
        const theTitle = title ? (title + (noTailTitle ? "" : (defaultSep + defaultTitle))) : defaultTitle;
        const theDescription = description
            ? description
            : defaultDescription;
        const theImage = _.get(image, "url", null) ? image.url : (image
            ? (image.startsWith("http") ? image : CDN_URL + image)
            : defaultImage);
        // Derive correct MIME type from either a filename or a raw extension
        const extracted = imageType ? this.extractType(imageType) : null;
        const extension = extracted && extracted[1] ? extracted[1].toLowerCase() : (imageType ? String(imageType).toLowerCase() : undefined);
        const theImageType = extension ? (this.getImageType(extension) || 'image/jpeg') : 'image/jpeg';
        const theKeyword =
            (keyword || keywords)
                ? (keyword
                    ? (isArray(keyword) ? keyword.join(',') : keyword)
                    : null
                        ||
                        keywords
                        ? (isArray(keywords) ? keywords.join(',') : keywords)
                        : null) + (noDefaultKeywords ? "" : ", " + defaultKeyword)
                : defaultKeyword;
        const metaTags = [
            {
                itemprop: 'name',
                content: theTitle
            }, {
                itemprop: 'description',
                content: theDescription
            }, {
                property: 'keywords',
                content: theKeyword
            }, {
                itemprop: 'image',
                content: theImage
            }, {
                name: 'description',
                content: theDescription
            }, {
                name: 'twitter:card',
                content: 'summary_large_image'
            }, {
                name: 'twitter:site',
                content: defaultTwitter
            }, {
                name: 'twitter:title',
                content: theTitle //theTitle.substr(0, theTitle.indexOf(" ONLINE STORE"))
            }, {
                name: 'twitter:description',
                content: theDescription
            }, {
                name: 'twitter:creator',
                content: twitter || defaultTwitter
            }, {
                name: 'twitter:image:src',
                content: theImage
            }, {
                property: 'og:title',
                content: theTitle //theTitle.substr(0, theTitle.indexOf(" ONLINE STORE"))
            }, {
                property: 'og:url',
                content: SITE_URL + pathname
            }, {
                property: 'og:image',
                content: theImage
            }, {
                property: 'og:image:secure_url',
                content: theImage
            }, {
                property: 'og:image:type',
                content: theImageType,
            }, {
                property: 'og:description',
                content: theDescription
            }, {
                property: 'og:site_name',
                content: defaultTitle
            }, {
                property: 'fb:app_id',
                content: FACEBOOK_APP_ID
            }, {
                property: 'fb:pages',
                content: FACEBOOK_PAGE_ID
            }, {
                property: 'og:type',
                content: 'website'
            }
        ];

        if (noCrawl) {
            metaTags.push({ name: 'robots', content: 'noindex, nofollow' });
        }
        if (published) {
            metaTags.push({ name: 'article:published_time', content: published });
        }
        if (updated) {
            metaTags.push({ name: 'article:modified_time', content: updated });
        }
        if (category) {
            metaTags.push({ name: 'article:section', content: category });
        }
        if (tags) {
            metaTags.push({ name: 'article:tag', content: tags });
        }
        if (getEnvVar('REACT_APP_DEV') === "development") {
            metaTags.push({ name: 'robots', content: 'noindex' })
        }
        if (imageWidth) {
            metaTags.push({ property: 'og:image:width', content: imageWidth });
        }
        if (imageHeight) {
            metaTags.push({ property: 'og:image:height', content: imageHeight })
        }

        return metaTags;
    }

    replaceAll(original, search, replacement) {
        var target = original;
        return target.replace(new RegExp(search, 'g'), replacement);
    }

    render() {
        const {
            children,
            id,
            className,
            style,
            product,
            // description,
            ...rest
        } = this.props;
        let desc = ''
        let m = []
        let buildMetaProps;
        let productComment;
        if (product) {
            if (product.seo_desc) {
                desc = product.seo_desc
            } else {
                const regex = /<[^>]*>([^<]*)/
                if ((m = regex.exec(product.mat_desc)) !== null) {
                    desc = m[1].substr(0, 200) + (m[1].length > 200 ? "..." : "")
                    desc = (desc && desc !== '') ? this.replaceAll(desc, '"', '&quot;') : ""
                }
            }
            if (!rest.description) {
                buildMetaProps = Object.assign(rest, { description: `${product.brand_en} ${product.mat_name} ${product.mat_name_th} ${desc}` || `${product.brand_en} ${product.mat_name} ${product.mat_name_th} ${defaultDescription}` })
            }
            let comments = product.comments ? (product.comments.data ? product.comments.data : null) : null;
            productComment = _.sortBy(comments, (c) => -c.rating)[0]
        } else {
            buildMetaProps = rest
            // desc = description
        }
        // const buildMetaProps = Object.assign(rest, product? {description: product.brand_en + ' - ' + product.mat_name + ' ' + ((product.mat_desc && product.mat_desc !== '')? product.mat_desc.replace(/<[^>]+>/g, ''): '')}: {})
        return (
            <div id={id} className={className} style={style}>
                <Helmet
                    htmlAttributes={{ lang: 'th', itemscope: undefined, itemtype: `http://schema.org/${rest.schema || 'WebPage'}` }}
                    title={rest.title ? rest.title + (!rest.title_suffix ? "" : (rest.noTailTitle ? " | BEAUTRIUM" : (defaultSep + defaultTitle))) : defaultTitle}
                    link={[
                        { rel: 'canonical', href: product ? SITE_URL + "/item/" + product.mat_id : SITE_URL + this.props.location.pathname },
                        // Preconnect to image CDN for faster image loading
                        { rel: 'preconnect', href: 'https://cdn.thebeautrium.com', crossOrigin: 'anonymous' },
                        { rel: 'dns-prefetch', href: 'https://cdn.thebeautrium.com' },
                        // Preload critical product images for LCP optimization
                        ...(product && product.image_1 ? [
                            {
                                rel: 'preload',
                                href: convertImageToWebP(product.image_1.startsWith('http') ? product.image_1 : CDN_URL + product.image_1),
                                as: 'image',
                                type: 'image/webp',
                                fetchpriority: 'high'
                            },
                            // Fallback preload for non-WebP browsers
                            // {
                            //     rel: 'preload',
                            //     href: product.image_1.startsWith('http') ? product.image_1 : CDN_URL + product.image_1,
                            //     as: 'image',
                            //     type: 'image/jpeg',
                            //     fetchpriority: 'high'
                            // }
                        ] : [])
                    ]}
                    meta={this.getMetaTags(buildMetaProps, this.props.location.pathname)}>
                    {product && !product.free && (
                        <script type="application/ld+json">{
                            JSON.stringify((() => {
                                const images = [
                                    product.image_1,
                                    product.image_2,
                                    product.image_3,
                                    product.image_4,
                                    product.image_5,
                                ].filter(Boolean);

                                const jsonLd = {
                                    "@context": "https://schema.org",
                                    "@type": "Product",
                                    name: `${product.mat_name}${product.volume ? ' ' + product.volume : ''}${product.variant_2 ? ' ' + product.variant_2 : ''}`,
                                    brand: product.brand_en,
                                    sku: product.item_id,
                                    image: images,
                                    offers: {
                                        "@type": "Offer",
                                        url: `https://thebeautrium.com/item/${product.mat_id}`,
                                        priceCurrency: "THB",
                                        price: Number(_.get(product, "net_price", 0) || _.get(product, "mat_price", 0) || 0),
                                        availability: `https://schema.org/${product.sold_out ? "SoldOut" : "InStock"}`,
                                        itemCondition: "https://schema.org/NewCondition",
                                    },
                                };

                                if (desc) {
                                    jsonLd.description = desc;
                                }
                                if (product.rating_count > 0) {
                                    jsonLd.aggregateRating = {
                                        "@type": "AggregateRating",
                                        ratingValue: Number(Math.min(product.rating, 5)),
                                        reviewCount: Number(product.rating_count),
                                        bestRating: 5,
                                    };
                                }
                                if (productComment) {
                                    jsonLd.review = {
                                        reviewBody: productComment.comment,
                                        author: {
                                            "@type": "Person",
                                            name: `${productComment.first_name} ${productComment.last_name}`,
                                        },
                                        dateCreated: moment(productComment.created_date).toISOString(),
                                    };
                                    if (productComment.rating) {
                                        jsonLd.review.reviewRating = {
                                            "@type": "Rating",
                                            ratingValue: Number(Math.min(productComment.rating, 5)),
                                            bestRating: 5,
                                        };
                                    }
                                }

                                return jsonLd;
                            })())
                        }</script>
                    )}
                    {rest.jsonLd && (
                        Array.isArray(rest.jsonLd)
                            ? rest.jsonLd.map((obj, idx) => (
                                <script key={`jsonld_${idx}`} type="application/ld+json">{JSON.stringify(obj)}</script>
                            ))
                            : <script type="application/ld+json">{JSON.stringify(rest.jsonLd)}</script>
                    )}
                </Helmet>
                {children}
            </div>
        );
    }
}

export default withRouter(Page);